import React, { useEffect, useMemo } from "react";
import uploadIcon from "@funded-here-interface/common/src/assets/cloud_upload_24px.png";
import { Group, Text } from "@mantine/core";
import { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  useAppendToBalance,
  useBankArray,
  useBusiness,
  useCerificateOfIncorporation,
  useFinancialStatementArray,
  useFundManagerLicenseArray,
  useInvestmentMandateArray,
  useProofArray,
  useSalaryProofArray,
  useShareArray,
  useSupportingDocumentsWithSuppliers,
  useCompanyConstitutionArray,
} from "../../hooks/handleBorFIleUpload";
import { RootState } from "../../store";
import { useStyle } from "./FileUpload.styles";
import useUploadOnboarding from "../../hooks/useUploadOnboarding";
import { File as FileEnum } from "@funded-here-interface/common/src/constant/enum";
import { toast } from "react-toastify";
import {
  UploadedFile,
  setBalance,
  setBankDoc,
  setBusinessDoc,
  setCertificateOfIncorporationDoc,
  setSupportingDocumentsWithSuppliersDoc,
  setFinancialStatementDoc,
  setFundManagerLicenseDoc,
  setInvestmentMandateDoc,
  setProofDoc,
  setSalaryProofDoc,
  setShareDoc,
  setCompanyConstitutionDoc,
} from "../../features/merchant/doc";
import useDeleteFile from "../../hooks/useDeleteFile";

type FileUploadComponentProps = {
  isEditable?: boolean;
  fileId: string;
  number: number;
  FileName: string;
  existingFiles?: UploadedFile[];
  onFileChange?: (files: UploadedFile[]) => void;
};

// File information text configuration
const FILE_INFO_TEXT: Partial<Record<FileEnum, string>> = {
  [FileEnum.CERTIFICATE_OF_INCORPORATION]:
    "Official certificate from your company registry showing the date and place of incorporation.",
  [FileEnum.SUPPORTING_DOCUMENTS_WITH_SUPPLIERS]:
    "Recent purchase orders, invoices, or contracts with suppliers related to commercial collaboration proposal.",
  [FileEnum.PROOF_OF_ADDRESS]:
    "Recent (within last 3 months) utility bill, bank statement, or government letter showing residential address for ALL:\n• Directors;\n• Shareholders with 25% and above shareholding percentage; and\n• Ultimate beneficial owners (UBOs).",
  [FileEnum.BUSINESS_PROFILE]:
    "Latest company profile from your company registry showing registration number, nature of business, business address, directors, and shareholders.",
  [FileEnum.BANK_STATEMENT]:
    "Bank statement header showing account name, number, and bank name. The account name must match the legal name of the company. This will be the bank account used to receive or remit funds.",
  [FileEnum.BALANCE_SHEET]:
    "Recent financial statements, management accounts, 6 to 12 months' bank statements, and/or proof of historical SKU sales.",
};

function FileUploadComponent({
  isEditable,
  fileId,
  number,
  FileName,
  existingFiles = [],
  onFileChange,
}: FileUploadComponentProps) {
  const { classes } = useStyle();
  const useUploadOnboardingMutation = useUploadOnboarding();
  const token = useSelector((state: RootState) => state.auth.token);
  const useDeleteFileMutation = useDeleteFile();
  const dispatch = useDispatch();

  // Centralized hook calls
  const { appendToProof, proof } = useProofArray();
  const { appendToBalance, balance } = useAppendToBalance();
  const { appendToShare, share } = useShareArray();
  const { appendToBusiness, business } = useBusiness();
  const { appendToBank, bank } = useBankArray();
  const { appendToSalaryProof, salaryProof } = useSalaryProofArray();
  const { appendToFinancialStatement, financialStatement } =
    useFinancialStatementArray();
  const { appendToInvestorMandate, investmentMandate } =
    useInvestmentMandateArray();
  const { appendToFundManagerLicense, fundManagerLicense } =
    useFundManagerLicenseArray();
  const { appendToCertificateOfIncorporation, certificateOfIncorporation } =
    useCerificateOfIncorporation();
  const {
    appendToSupportingDocumentsWithSuppliers,
    supportingDocumentsWithSuppliers,
  } = useSupportingDocumentsWithSuppliers();
  const { appendToCompanyConstitution, companyConstitution } =
    useCompanyConstitutionArray();

  // Unified file configuration mapping
  const fileConfig = useMemo<
    Partial<
      Record<
        FileEnum,
        {
          state: UploadedFile[];
          append: (file: UploadedFile, state: UploadedFile[]) => void;
          dispatch: (files: UploadedFile[]) => void;
        }
      >
    >
  >(
    () => ({
      [FileEnum.BUSINESS_PROFILE]: {
        state: business,
        append: appendToBusiness,
        dispatch: (files: UploadedFile[]) => dispatch(setBusinessDoc(files)),
      },
      [FileEnum.SHAREHOLDERS_DIRECTORS_REGISTERS]: {
        state: share,
        append: appendToShare,
        dispatch: (files: UploadedFile[]) => dispatch(setShareDoc(files)),
      },
      [FileEnum.BALANCE_SHEET]: {
        state: balance,
        append: appendToBalance,
        dispatch: (files: UploadedFile[]) => dispatch(setBalance(files)),
      },
      [FileEnum.PROOF_OF_ADDRESS]: {
        state: proof,
        append: appendToProof,
        dispatch: (files: UploadedFile[]) => dispatch(setProofDoc(files)),
      },
      [FileEnum.BANK_STATEMENT]: {
        state: bank,
        append: appendToBank,
        dispatch: (files: UploadedFile[]) => dispatch(setBankDoc(files)),
      },
      [FileEnum.SALARY_PROOF]: {
        state: salaryProof,
        append: appendToSalaryProof,
        dispatch: (files: UploadedFile[]) => dispatch(setSalaryProofDoc(files)),
      },
      [FileEnum.FINANCIAL_STATEMENT]: {
        state: financialStatement,
        append: appendToFinancialStatement,
        dispatch: (files: UploadedFile[]) =>
          dispatch(setFinancialStatementDoc(files)),
      },
      [FileEnum.INVESTMENT_MANDATE]: {
        state: investmentMandate,
        append: appendToInvestorMandate,
        dispatch: (files: UploadedFile[]) =>
          dispatch(setInvestmentMandateDoc(files)),
      },
      [FileEnum.FUND_MANAGER_LICENSE]: {
        state: fundManagerLicense,
        append: appendToFundManagerLicense,
        dispatch: (files: UploadedFile[]) =>
          dispatch(setFundManagerLicenseDoc(files)),
      },
      [FileEnum.CERTIFICATE_OF_INCORPORATION]: {
        state: certificateOfIncorporation,
        append: appendToCertificateOfIncorporation,
        dispatch: (files: UploadedFile[]) =>
          dispatch(setCertificateOfIncorporationDoc(files)),
      },
      [FileEnum.SUPPORTING_DOCUMENTS_WITH_SUPPLIERS]: {
        state: supportingDocumentsWithSuppliers,
        append: appendToSupportingDocumentsWithSuppliers,
        dispatch: (files: UploadedFile[]) =>
          dispatch(setSupportingDocumentsWithSuppliersDoc(files)),
      },
      [FileEnum.COMPANY_CONSTITUTION]: {
        state: companyConstitution,
        append: appendToCompanyConstitution,
        dispatch: (files: UploadedFile[]) =>
          dispatch(setCompanyConstitutionDoc(files)),
      },
    }),
    [
      business,
      share,
      balance,
      proof,
      bank,
      salaryProof,
      financialStatement,
      investmentMandate,
      fundManagerLicense,
      certificateOfIncorporation,
      supportingDocumentsWithSuppliers,
      companyConstitution,
      appendToBusiness,
      appendToShare,
      appendToBalance,
      appendToProof,
      appendToBank,
      appendToSalaryProof,
      appendToFinancialStatement,
      appendToInvestorMandate,
      appendToFundManagerLicense,
      appendToCertificateOfIncorporation,
      appendToSupportingDocumentsWithSuppliers,
      appendToCompanyConstitution,
      dispatch,
    ]
  );

  // Helper functions using unified config
  const getFileState = (file: FileEnum) => {
    return fileConfig[file]?.state ?? null;
  };

  const appendFile = (file: UploadedFile) => {
    const config = fileConfig[fileId as FileEnum];
    if (config) {
      config.append(file, config.state);
    }
  };

  const dispatchFileUpdate = (updatedFiles: UploadedFile[]) => {
    const config = fileConfig[fileId as FileEnum];
    if (config) {
      config.dispatch(updatedFiles);
    }
  };

  const [selectedFiles, setSelectedFiles] = useState<UploadedFile[]>(
    existingFiles?.length > 0
      ? existingFiles
      : getFileState(fileId as FileEnum) ?? []
  );

  useEffect(() => {
    setSelectedFiles(existingFiles || []);
  }, [existingFiles]);

  const handleUploadFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e?.target?.files && e?.target?.files?.length > 0) {
      const file = e.target.files[0];

      const fileNameExists = selectedFiles.some(
        (selectedFile) => selectedFile.name === file.name
      );

      if (fileNameExists) {
        toast.error("A file with the same name already exists.");
        return;
      }

      useUploadOnboardingMutation.mutate(
        { file, token, name: `${FileName}-${file.name}` },
        {
          onSuccess: (filePath: string) => {
            const newFile: UploadedFile = {
              name: file.name,
              path: filePath,
            };
            appendFile(newFile);
            const updatedFiles = [
              ...selectedFiles,
              { name: file.name, path: filePath },
            ];
            setSelectedFiles(updatedFiles);
            dispatchFileUpdate(updatedFiles); // Update Redux state
            onFileChange?.(updatedFiles); // Notify parent of file change
            e.target.value = "";
          },
          onError: (error) => {
            e.target.value = "";
            toast.error((error as Error).message);
          },
        }
      );
    }
  };

  const handleDeleteFile = (filePath: string) => {
    useDeleteFileMutation.mutate(
      { token, filePath },
      {
        onSuccess: () => {
          const updatedFiles = selectedFiles.filter(
            (file) => file.path !== filePath
          );
          dispatchFileUpdate(updatedFiles);
          setSelectedFiles(updatedFiles);
          onFileChange?.(updatedFiles); // Notify parent of file change
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      }
    );
  };

  return (
    <>
      <div className={classes.docContainer}>
        <Group style={{ padding: 20 }}>
          <Text className={classes.docName}>{`${number}. ${FileName}`}</Text>
          <div className={classes.uploadButtonContainer}>
            <label htmlFor={fileId} className={classes.label}>
              <input
                type="file"
                name={fileId}
                id={fileId}
                accept=".pdf"
                style={{ display: "none" }}
                onChange={handleUploadFile}
                disabled={!isEditable}
              />
              <img src={uploadIcon} alt="upload" />{" "}
              <span style={{ paddingTop: "-15px" }}> Upload </span>
            </label>
          </div>
        </Group>

        {FILE_INFO_TEXT[fileId as FileEnum] && (
          <Text className={classes.additionalInfo}>
            {FILE_INFO_TEXT[fileId as FileEnum]?.split("\n").map((line, i) => (
              <React.Fragment key={i}>
                {line}
                {i <
                  (FILE_INFO_TEXT[fileId as FileEnum]?.split("\n").length ??
                    0) -
                    1 && <br />}
              </React.Fragment>
            ))}
          </Text>
        )}
      </div>
      {selectedFiles.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <Text className={classes.uploadedFileHeader}>Uploaded Files:</Text>
          {selectedFiles?.map((file) => (
            <Group>
              <div className={classes.uploadedFileName}>
                <Text className={classes.uploadedDocName}>{file.name}</Text>
                <Text
                  onClick={() => isEditable && handleDeleteFile(file.path)}
                  className={classes.removeText}
                >
                  Remove
                </Text>
              </div>
            </Group>
          ))}
        </div>
      )}
    </>
  );
}

export default FileUploadComponent;
